import {
  createEntry,
  deleteEntry,
  fetchChangeLog,
  fetchPunchHistory,
  fetchPreview,
  fetchRevisionByID,
  fetchRevisions,
  fetchState,
  submitForEmployee,
  updateEntry,
} from '../dataLayer/timesheetEdit';
import {
  PayrollEditEntryKind,
  PayrollEntryBody,
  PayrollEntryResult,
  PayrollSubmitTimesheetRequest,
  PayrollSubmitTimesheetResponse,
  PayrollTimesheetState,
  PunchHistoryResponse,
  TimesheetChangeLogResponse,
  TimesheetPreview,
  TimesheetRevisionDetailResponse,
  TimesheetRevisionListResponse,
} from '../interfaces/TimesheetEditInterface';

/**
 * Payroll Timesheet Modifications — manager (Milestone 1, M1-B2).
 *
 * The Milestone 1 surface is loadState / submitForEmployee / the three history
 * reads. The entry CRUD methods are DECLARED here so the manager surface is
 * stable for the M2 editor, but they throw until M2-A1 lands the endpoints —
 * nothing in M1 may call them.
 *
 * Errors are logged and rethrown with their axios identity intact: the 409
 * (employee submitted mid-flow), 400 (stale ID arrays), and 403 responses are
 * designed UI states the caller matches on `err.response.status`.
 */
class TimesheetEditManager {

  /** The editable state / `{ exists, timesheetID }` redirect for one employee + pay period. */
  async loadState(employeeID: number, payPeriodID: number): Promise<PayrollTimesheetState> {
    try {
      const result = await fetchState(employeeID, payPeriodID);
      console.log('Payroll timesheet state loaded:', result);
      return result;
    } catch (error) {
      console.error('Error loading payroll timesheet state:', error);
      throw error;
    }
  }

  /**
   * Pre-submission running totals — the server's real hour calculation without
   * persisting (closed decision 2026-08-09: the unsubmitted drill-down's KPI and
   * matrix OT/DT numbers come from HERE, never from client arithmetic).
   */
  async loadPreview(employeeID: number, payPeriodID: number): Promise<TimesheetPreview> {
    try {
      const result = await fetchPreview(employeeID, payPeriodID);
      console.log('Payroll timesheet preview loaded:', result);
      return result;
    } catch (error) {
      console.error('Error loading payroll timesheet preview:', error);
      throw error;
    }
  }

  /** Submit the pay period on the employee's behalf (recorded as PAYROLL). */
  async submitForEmployee(
    request: PayrollSubmitTimesheetRequest
  ): Promise<PayrollSubmitTimesheetResponse> {
    try {
      const result = await submitForEmployee(request);
      console.log('Payroll submit successful:', result);
      return result;
    } catch (error) {
      console.error('Error during payroll submit:', error);
      throw error;
    }
  }

  /** Every field-level change for one employee + pay period, chronological. */
  async getChangeLog(employeeID: number, payPeriodID: number): Promise<TimesheetChangeLogResponse> {
    try {
      const result = await fetchChangeLog(employeeID, payPeriodID);
      console.log('Timesheet change log loaded:', result);
      return result;
    } catch (error) {
      console.error('Error loading timesheet change log:', error);
      throw error;
    }
  }

  /** TAS-365: every punch of one employee + pay period that has change history. */
  async getPunchHistory(employeeID: number, payPeriodID: number): Promise<PunchHistoryResponse> {
    try {
      return await fetchPunchHistory(employeeID, payPeriodID);
    } catch (error) {
      console.error('Error loading punch history:', error);
      throw error;
    }
  }

  /** The revision list (no payloads) for one employee + pay period. */
  async getRevisions(employeeID: number, payPeriodID: number): Promise<TimesheetRevisionListResponse> {
    try {
      const result = await fetchRevisions(employeeID, payPeriodID);
      console.log('Timesheet revisions loaded:', result);
      return result;
    } catch (error) {
      console.error('Error loading timesheet revisions:', error);
      throw error;
    }
  }

  /** One revision with its full parsed payload. */
  async getRevisionByID(timesheetRevisionSnapshotID: number): Promise<TimesheetRevisionDetailResponse> {
    try {
      const result = await fetchRevisionByID(timesheetRevisionSnapshotID);
      console.log('Timesheet revision loaded:', result);
      return result;
    } catch (error) {
      console.error('Error loading timesheet revision:', error);
      throw error;
    }
  }

  // ── Milestone 2 — entry CRUD (§8; 21 routes over 7 kinds) ───────────────────
  // Every call carries the page's per-load editSessionID GUID inside its body
  // (delete takes it explicitly). Errors rethrow with their axios identity
  // intact — the 409 (period submitted mid-edit), the date-range 400s, and the
  // task-budget 400 are designed UI states the editor matches on
  // `err.response.status` / `err.response.data.message`.

  /** Create one entry of the given kind. Body must include `editSessionID`. */
  async createEntry(
    kind: PayrollEditEntryKind,
    payload: PayrollEntryBody
  ): Promise<PayrollEntryResult> {
    try {
      const result = await createEntry(kind, payload);
      console.log(`Payroll ${kind} entry created:`, result);
      return result;
    } catch (error) {
      console.error(`Error creating payroll ${kind} entry:`, error);
      throw error;
    }
  }

  /** Update one entry (full per-kind body; server change-logs only moved fields). */
  async updateEntry(
    kind: PayrollEditEntryKind,
    entityID: number,
    payload: PayrollEntryBody
  ): Promise<PayrollEntryResult> {
    try {
      const result = await updateEntry(kind, entityID, payload);
      console.log(`Payroll ${kind} entry ${entityID} updated:`, result);
      return result;
    } catch (error) {
      console.error(`Error updating payroll ${kind} entry ${entityID}:`, error);
      throw error;
    }
  }

  /** Delete one entry. The task kind restores the claimed budget server-side. */
  async deleteEntry(
    kind: PayrollEditEntryKind,
    entityID: number,
    editSessionID: string
  ): Promise<PayrollEntryResult> {
    try {
      const result = await deleteEntry(kind, entityID, editSessionID);
      console.log(`Payroll ${kind} entry ${entityID} deleted:`, result);
      return result;
    } catch (error) {
      console.error(`Error deleting payroll ${kind} entry ${entityID}:`, error);
      throw error;
    }
  }

}

// Exporting an instance to use throughout the application (house pattern).
export default new TimesheetEditManager();
